import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useProject } from '@/contexts/ProjectContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { queryKeys } from '@/lib/queryKeys';
import { threatcatService } from '@/services/threatcatService';
import { ThreatCatAuthError } from '@/lib/errors';
import { projectService } from '@/services/projectService';

interface SyncProjectInput {
  projectId: number;
  projectVersionId: number;
}

export function useProjectSync() {
  const queryClient = useQueryClient();
  const { getCookie, disconnect } = useThreatCat();
  const { projectId, detail, setSyncState } = useProject();

  const syncProject = useCallback(async ({ projectId, projectVersionId }: SyncProjectInput) => {
    // Read the cookie fresh instead of relying on a value closed over at the
    // last render: callers may invoke this right after `connect()` resolves,
    // before React has re-rendered with the new `cookie` state.
    const cookie = getCookie();
    if (!cookie) throw new Error('ThreatCat is not connected');
    setSyncState('syncing');
    try {
      await threatcatService.fetchThreatCatProjectExport(cookie, projectVersionId, projectId);
      const detailKey = queryKeys.projects.detail(projectId);
      // Do not reuse a detail request started before the export completed.
      await queryClient.cancelQueries({ queryKey: detailKey, exact: true });
      await queryClient.invalidateQueries({
        queryKey: detailKey,
        exact: true,
        refetchType: 'none',
      });
      const [updatedDetail] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: detailKey,
          queryFn: () => projectService.get(projectId),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.all, exact: true }),
      ]);
      setSyncState('idle');
      return updatedDetail;
    } catch (error) {
      if (error instanceof ThreatCatAuthError) disconnect();
      setSyncState('error', error instanceof Error ? error.message : 'Synchronization failed');
      throw error;
    }
  }, [getCookie, disconnect, queryClient, setSyncState]);

  const preflightSync = useCallback(async () => {
    if (!projectId || !detail?.project_version_id) {
      throw new Error('Project has no synchronized ThreatCat version');
    }
    return syncProject({
      projectId,
      projectVersionId: detail.project_version_id,
    });
  }, [detail?.project_version_id, projectId, syncProject]);

  return { syncProject, preflightSync };
}
